import React, { useState, useEffect, useRef } from "react";
import {
  LayoutDashboard,
  Users,
  HardDrive,
  FileText,
  CalendarDays,
  UserCog,
  Brain,
  Zap,
  Settings,
  MessageSquare,
  MoreHorizontal,
  X,
  Palette,
  ChevronRight,
  ClipboardList,
  Timer,
  CalendarCheck,
  Package,
  Receipt,
  Car,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { usePermisos } from "../../hooks/usePermisos";
import ThemeCustomizer from "../theme/ThemeCustomizer";

/* ─── Icon map ─── */
const iconMap = {
  dashboard: LayoutDashboard,
  control_horas: Timer,
  calendario_visitas: CalendarCheck,
  clientes: Users,
  equipos: HardDrive,
  ordenes: FileText,
  presupuestos: ClipboardList,
  agenda: CalendarDays,
  agendamientos_inteligentes: Brain,
  usuarios: UserCog,
  admin_avanzado: Settings,
  pendientes: FileText,
  tickets: MessageSquare,
  recepciones_equipos: Package,
  entregas_documentos: Receipt,
  movilidad: Car,
  dashboard_cliente: LayoutDashboard,
  mis_ordenes: FileText,
  mis_equipos: HardDrive,
  mis_agendamientos: CalendarDays,
  mis_horas: CalendarDays,
  monitor_servicios: Zap,
};

/* ─── Module definitions (same as SmartSidebar) ─── */
const modulosDisponibles = {
  dashboard: { key: "dashboard", label: "Dashboard" },
  control_horas: { key: "control_horas", label: "Control Horas" },
  calendario_visitas: { key: "calendario_visitas", label: "Calendario" },
  clientes: { key: "clientes", label: "Clientes" },
  equipos: { key: "equipos", label: "Equipos" },
  ordenes: { key: "ordenes", label: "Órdenes" },
  presupuestos: { key: "presupuestos", label: "Presupuestos" },
  agenda: { key: "agenda", label: "Agenda" },
  agendamientos_inteligentes: { key: "agendamientos_inteligentes", label: "IA Agenda" },
  usuarios: { key: "usuarios", label: "Usuarios" },
  admin_avanzado: { key: "admin_avanzado", label: "Panel Avanzado" },
  pendientes: { key: "pendientes", label: "Pendientes" },
  tickets: { key: "tickets", label: "Tickets" },
  recepciones_equipos: { key: "recepciones_equipos", label: "Recep. Equipos" },
  entregas_documentos: { key: "entregas_documentos", label: "Entrega Docs." },
  movilidad: { key: "movilidad", label: "Movilidad" },
  dashboard_cliente: { key: "dashboard_cliente", label: "Inicio" },
  mis_ordenes: { key: "mis_ordenes", label: "Mis Órdenes" },
  mis_equipos: { key: "mis_equipos", label: "Mis Equipos" },
  mis_agendamientos: { key: "mis_agendamientos", label: "Mis Agenda" },
  mis_horas: { key: "mis_horas", label: "Mis Horas" },
  monitor_servicios: { key: "monitor_servicios", label: "Estado" },
};

const itemsCliente = [
  "dashboard_cliente", "monitor_servicios", "tickets",
  "mis_ordenes", "mis_equipos", "mis_agendamientos", "mis_horas",
];

/* Maximum tabs in bottom bar (rest go to "More" sheet) */
const MAX_TABS = 4;

export default function MobileBottomNav({ seccion, onChange }) {
  const { settings, currentTheme, getThemeClasses } = useTheme();
  const { modulos, loading, puedeVer } = usePermisos();
  const { user } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const currentTranslate = useRef(0);

  /* ─── Build items list (same logic as SmartSidebar) ─── */
  const rol = user?.rol;
  const esSuperAdmin = user?.username === "admin";

  let items = [];

  if (rol === "admin") {
    const allAdmin = [
      "dashboard", "pendientes", "tickets", "control_horas", "calendario_visitas",
      "clientes", "equipos", "ordenes", "presupuestos",
      "agenda", "agendamientos_inteligentes", "usuarios", "admin_avanzado",
      "recepciones_equipos", "entregas_documentos", "movilidad",
    ];
    if (esSuperAdmin || loading || modulos.length === 0) {
      items = allAdmin.map(k => modulosDisponibles[k]);
    } else {
      const mapeo = { agendamientos: "agenda", panelAvanzado: "admin_avanzado", reportes: "control_horas" };
      items = modulos
        .map(m => mapeo[m] || m)
        .filter(k => modulosDisponibles[k])
        .map(k => modulosDisponibles[k]);
    }
  } else if (rol === "cliente") {
    items = (loading ? itemsCliente.slice(0, 1) : itemsCliente.filter(k => puedeVer(k)))
      .map(k => modulosDisponibles[k]);
  } else if (rol === "tecnico") {
    const defaultTecnico = [
      "dashboard", "tickets", "ordenes", "control_horas", "calendario_visitas",
      "presupuestos", "clientes", "equipos", "agenda",
      "recepciones_equipos", "entregas_documentos", "movilidad",
    ];
    if (loading) {
      items = [modulosDisponibles.dashboard];
    } else if (modulos.length === 0) {
      items = defaultTecnico.map(k => modulosDisponibles[k]).filter(Boolean);
    } else {
      const mapeo = { agendamientos: "agenda", panelAvanzado: "admin_avanzado", reportes: "control_horas" };
      items = modulos
        .map(m => mapeo[m] || m)
        .filter(k => modulosDisponibles[k])
        .map(k => modulosDisponibles[k]);
    }
  } else {
    items = [modulosDisponibles.dashboard];
  }

  /* Split into tab bar items + overflow */
  const tabItems = items.slice(0, MAX_TABS);
  const overflowItems = items.slice(MAX_TABS);
  const hasOverflow = overflowItems.length > 0;

  /* Is current section in overflow? Highlight "More" tab */
  const seccionInOverflow = overflowItems.some(i => i.key === seccion);

  /* ─── Swipe-to-dismiss for bottom sheet ─── */
  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    const deltaY = e.touches[0].clientY - startY.current;
    if (deltaY > 0 && sheetRef.current) {
      currentTranslate.current = deltaY;
      sheetRef.current.style.transform = `translateY(${deltaY}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (currentTranslate.current > 100) {
      setSheetOpen(false);
    }
    if (sheetRef.current) {
      sheetRef.current.style.transform = "";
    }
    currentTranslate.current = 0;
  };

  /* Close sheet on outside tap */
  useEffect(() => {
    if (!sheetOpen) return;
    const close = (e) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target)) {
        setSheetOpen(false);
      }
    };
    // Small delay to avoid immediate-close from the tap that opened it
    const timer = setTimeout(() => document.addEventListener("touchstart", close), 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("touchstart", close);
    };
  }, [sheetOpen]);

  /* Lock body scroll when sheet is open */
  useEffect(() => {
    if (sheetOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [sheetOpen]);

  const renderIcon = (key, size = 22, active = false) => {
    const Icon = iconMap[key] || FileText;
    return <Icon size={size} strokeWidth={active ? 2.2 : 1.6} />;
  };

  return (
    <>
      {/* ─── Bottom Tab Bar ─── */}
      <nav
        className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white border-t border-gray-200 safe-area-bottom"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex items-stretch justify-around h-14">
          {tabItems.map(({ key, label }) => {
            const active = seccion === key;
            return (
              <button
                key={key}
                onClick={() => onChange(key)}
                className={`flex flex-col items-center justify-center flex-1 gap-0.5 transition-colors duration-150 relative
                  ${active ? "text-emerald-600" : "text-gray-400 active:text-gray-600"}`}
              >
                {active && (
                  <span className="absolute top-0 inset-x-3 h-[3px] rounded-b-full bg-emerald-500" />
                )}
                {renderIcon(key, 22, active)}
                <span className={`text-[10px] leading-tight ${active ? "font-semibold" : "font-medium"}`}>
                  {label.length > 10 ? label.slice(0, 9) + "…" : label}
                </span>
              </button>
            );
          })}

          {/* "More" tab */}
          {hasOverflow && (
            <button
              onClick={() => setSheetOpen(true)}
              className={`flex flex-col items-center justify-center flex-1 gap-0.5 transition-colors duration-150 relative
                ${seccionInOverflow ? "text-emerald-600" : "text-gray-400 active:text-gray-600"}`}
            >
              {seccionInOverflow && (
                <span className="absolute top-0 inset-x-3 h-[3px] rounded-b-full bg-emerald-500" />
              )}
              <MoreHorizontal size={22} strokeWidth={seccionInOverflow ? 2.2 : 1.6} />
              <span className={`text-[10px] leading-tight ${seccionInOverflow ? "font-semibold" : "font-medium"}`}>
                Más
              </span>
            </button>
          )}
        </div>
      </nav>

      {/* ─── Bottom Sheet (More) ─── */}
      {sheetOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fadeIn"
            onClick={() => setSheetOpen(false)}
          />

          {/* Sheet */}
          <div
            ref={sheetRef}
            className="absolute bottom-0 inset-x-0 bg-white rounded-t-2xl shadow-2xl animate-slideUp max-h-[70vh] flex flex-col"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-base font-semibold text-gray-900">Más opciones</h3>
              <button
                onClick={() => setSheetOpen(false)}
                className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Items grid */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3">
              <div className="grid grid-cols-3 gap-2">
                {overflowItems.map(({ key, label }) => {
                  const active = seccion === key;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        onChange(key);
                        setSheetOpen(false);
                      }}
                      className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all duration-150
                        ${active
                          ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200"
                          : "text-gray-600 hover:bg-gray-50 active:bg-gray-100"
                        }`}
                    >
                      {renderIcon(key, 24, active)}
                      <span className={`text-xs text-center leading-tight ${active ? "font-semibold" : "font-medium"}`}>
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Theme customizer shortcut */}
              <div className="mt-4 pt-3 border-t border-gray-100">
                <button
                  onClick={() => {
                    setSheetOpen(false);
                    setShowCustomizer(true);
                  }}
                  className="flex items-center gap-3 w-full px-3 py-3 rounded-xl text-gray-600 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <Palette size={20} className="text-purple-500" />
                  <span className="text-sm font-medium flex-1 text-left">Personalizar Tema</span>
                  <ChevronRight size={16} className="text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Theme Customizer */}
      <ThemeCustomizer
        isOpen={showCustomizer}
        onClose={() => setShowCustomizer(false)}
      />
    </>
  );
}
